import { get } from "lodash";
import { ACTIVITY_TYPES } from "utils/constants";

const INITIATOR_BROKER = "BROKER";
const INITIATOR_AUTOMATIC = "AUTOMATIC";

const buildPersonName = (person) => ({
  firstname: get(person, "firstname", ""),
  lastname: get(person, "lastname", ""),
});

/**
 * For `projectDistributed` rows, build a synthetic initiator describing
 * who triggered the distribution (system vs human) and a recipient
 * describing the advisor receiving the project.
 *
 * - Automatic distribution (`data.automatic === true`, recorded by the
 *   external project flow / `assignProjectAttribution`): no acting user;
 *   we surface an `AUTOMATIC` initiator badge and the recipient name.
 * - Manual distribution (`data.automatic === false`, recorded by the
 *   distribution addon when an admin reassigns a project): we surface a
 *   `BROKER` initiator with the actor name (`data.distributedBy`) and the
 *   recipient name (`data.distributedTo`).
 */
const buildDistributionMeta = (row) => {
  const isAutomatic = get(row, "data.automatic") === true;
  const distributedTo = get(row, "data.distributedTo", null);
  const distributedBy = get(row, "data.distributedBy", null);
  const initiator = isAutomatic
    ? { type: INITIATOR_AUTOMATIC }
    : distributedBy
      ? { type: INITIATOR_BROKER, ...buildPersonName(distributedBy) }
      : null;
  const recipient = distributedTo ? buildPersonName(distributedTo) : null;
  return { initiator, recipient };
};

/** Matches {@link buildContactActivityBuckets} qualification flatMap length. */
const countQualificationActivitiesFromQualifiers = (qualifiers = []) =>
  qualifiers.reduce(
    (total, qualifier) => total + (qualifier.qualifications?.length ?? 0),
    0,
  );

/**
 * Lifecycle actions whose initiator (`PROSPECT` / `BROKER`) is recorded
 * inside `data.initiator` by the API. The badge + advisor name are pulled
 * from that snapshot when present.
 *
 * Duplicated projects expose `projectCreated` *and* `projectDuplicated`
 * (CA-07 / CA-08): the former is back-dated and attributed to the
 * original initiator, the latter records who triggered the duplication.
 */
const INITIATOR_DRIVEN_ACTIONS = new Set([
  "projectCreated",
  "quoteSent",
  "quoteFollowUpSent",
  "signatureInitiated",
  "sentToManagement",
  "projectDuplicated",
  /**
   * Manual GED signature is recorded by the conseiller; `data.initiator` +
   * `data.initiator.date` (aligned on subscription `signedDate`) match the
   * other lifecycle rows so the timeline sorts and formats like Paris like
   * `quoteSent` / `sentToManagement` instead of relying on `createdDate`
   * alone (Postgres `timestamp without time zone` drift — see block below).
   */
  "manualSignatureCompleted",
]);

/**
 * Builds the activity buckets used in the contact drawer Activités tab.
 *
 * Status / lifecycle activities are sourced exclusively from `UserActivity`
 * rows: every milestone (project creation, quote sent, signature initiated,
 * documents signed, sent to management, distribution, takeover, duplication)
 * is recorded server-side with a stable `createdDate`, an optional
 * `data.initiator` snapshot (PROSPECT vs BROKER), and the related advisor
 * payload (`distributedTo` / `takenOverBy`). The hook ordering uses
 * `createdDate DESC`; this builder only shapes rows for the renderer.
 *
 * Merging across buckets and final sorting are done by the caller.
 */
const buildContactActivityBuckets = ({
  qualifiers = [],
  calls = [],
  emailActivityRows = [],
  systemActivityRows = [],
  projectAuthor,
}) => {
  const qualificationActivities = qualifiers.flatMap(
    ({ qualifications, name }) =>
      qualifications.map(({ createdDate }) => ({
        activityType: ACTIVITY_TYPES.QUALIFICATION,
        status: name,
        date: createdDate,
        author: projectAuthor,
      })),
  );

  const statusActivities = systemActivityRows.map((row) => {
    const baseDate = row.createdDate;
    if (INITIATOR_DRIVEN_ACTIONS.has(row.action)) {
      const initiator = get(row, "data.initiator", {});
      const isBrokerInitiator = initiator?.type === INITIATOR_BROKER;
      /**
       * Prefer the explicit ISO UTC `data.initiator.date` over the
       * auto-stamped `createdDate`: the latter is stored in a Postgres
       * `timestamp without time zone` column and round-trips through the
       * `pg` driver in the Node process timezone, which can shift the
       * displayed value by the local UTC offset (visible when comparing
       * an auto-stamped row to a back-dated one — see CA-08 duplications).
       * `data.initiator.date` is always written as a clean UTC ISO string
       * by the API and is safe to format directly to Europe/Paris.
       */
      return {
        activityType: ACTIVITY_TYPES.STATUS,
        status: row.action,
        date: get(initiator, "date") || baseDate,
        initiator,
        author: isBrokerInitiator
          ? {
              firstname: get(initiator, "firstname", ""),
              lastname: get(initiator, "lastname", ""),
            }
          : null,
        data: row.data,
      };
    }
    if (row.action === "projectDistributed") {
      const { initiator, recipient } = buildDistributionMeta(row);
      return {
        activityType: ACTIVITY_TYPES.STATUS,
        status: row.action,
        date: baseDate,
        initiator,
        author:
          initiator?.type === INITIATOR_BROKER
            ? buildPersonName(initiator)
            : null,
        recipient,
        data: row.data,
      };
    }
    if (row.action === "projectTakenOver") {
      const advisorInfo = get(row, "data.takenOverBy", null);
      return {
        activityType: ACTIVITY_TYPES.STATUS,
        status: row.action,
        date: baseDate,
        author: advisorInfo ? buildPersonName(advisorInfo) : null,
        data: row.data,
      };
    }
    if (row.action === "electronicSignatureCompleted") {
      const signedDate = get(row, "data.signedDate");
      return {
        activityType: ACTIVITY_TYPES.STATUS,
        status: row.action,
        date: signedDate || baseDate,
        author: null,
        data: row.data,
      };
    }
    return {
      activityType: ACTIVITY_TYPES.STATUS,
      status: row.action,
      date: baseDate,
      author: null,
      data: row.data,
    };
  });

  const callActivities = calls.map((call) => ({
    activityType: ACTIVITY_TYPES.CALL,
    date: call.updatedDate || call.startedAt || call.dialedAt,
    callData: call,
  }));

  const emailActivities = emailActivityRows.map((row) => ({
    activityType: ACTIVITY_TYPES.EMAIL_SENT,
    date: row.createdDate,
    emailActivity: {
      id: row.id,
      subject: get(row, "data.input.subject", ""),
      to: get(row, "data.input.to", []) || [],
      cc: get(row, "data.input.cc"),
      user: row.user,
    },
  }));

  return {
    qualificationActivities,
    statusActivities,
    callActivities,
    emailActivities,
  };
};

export {
  buildContactActivityBuckets,
  countQualificationActivitiesFromQualifiers,
};
